import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@xstate/react';
import {
  Alert,
  Box,
  Button,
  Card,
  Column,
  CreditCard,
  Heading,
  Modal,
  Row,
} from '@chargify/custom-ui-components';
import { useToastMessages } from '@chargify/toasts';

import { paymentProfilesSelectors } from '../../../machines';
import { PaymentProfilesContext } from '../../../contexts';
import {
  PaymentProfileActions,
  useChangePaymentProfile,
  usePaymentProfileAvailableActions,
  useServices,
} from '../../../hooks';
import { changePaymentProfileSelectors } from '../../../machines/changePaymentProfile';
import BillingAddress from '../BillingAddress';
import { PAYMENT_PROFILE_STATUS } from '../../../containers/PaymentProfileContainer/constants';

type PreviewPaymentProfileProps = {
  /**
   * Product catalog identifier
   */
  catalogId: number;
  /**
   * Subscription identifier
   */
  subscriptionId: number;
  /**
   * Modal display mode
   */
  displayMode: 'layer' | 'window';
  /**
   * Close event handler
   */
  onClose: (successUpdate?: boolean) => void;
  /**
   * Payment profile identifier
   */
  paymentProfileId: number;
};

const Content = ({
  subscriptionId,
  catalogId,
  onClose,
  displayMode,
  paymentProfileId,
}: PreviewPaymentProfileProps) => {
  const { t } = useTranslation(['subscriptions-manager', 'payment-profile']);
  const toastService = useToastMessages();
  const { modalsService } = useServices();

  const { paymentProfilesService } = useContext(PaymentProfilesContext);

  const { creditCard, address, type, status } = useSelector(
    paymentProfilesService,
    paymentProfilesSelectors.getPaymentProfilesById(paymentProfileId)
  );
  const paymentProfileActions = usePaymentProfileAvailableActions(status);

  const onDeletePaymentProfile = () => {
    onClose(true);
    modalsService.send({
      type: 'SHOW_DELETE_PAYMENT_PROFILE',
      paymentProfileId,
      subscriptionId,
      catalogId,
    });
  };
  const onSuccessfulChange = () => {
    toastService.show({
      message: t('payment_profile.success_payment_method_change'),
      level: 'success',
    });
    onClose(true);
  };

  const changePaymentProfileService = useChangePaymentProfile({
    subscriptionId,
    catalogId,
    paymentProfileId,
    onChangeSuccess: onSuccessfulChange,
  });

  const { isError, isLoading } = useSelector(
    changePaymentProfileService,
    changePaymentProfileSelectors.getChangePaymentProfileStatus
  );

  const isAddressEmpty = useMemo(
    () => Object.values(address).every((value) => !value),
    [address]
  );
  return (
    <Card border={displayMode === 'layer' ? 'sharp' : 'rounded'}>
      <Card.Header>
        <Modal.Title
          onClose={onClose}
          title={t('payment_profile.preview_method_title')}
        />
      </Card.Header>
      <Card.Body>
        {isError ? (
          <Alert color="danger" title={t('payment_profile.unexpected_problem')}>
            <Alert.TextMessage>
              {t('payment_profile.unable_to_change_payment_method')}
            </Alert.TextMessage>
          </Alert>
        ) : (
          <>
            <Row>
              <Column span={[24, 6]}>
                <Heading variant={'h4'}>
                  {t('payment_profile.preview_method_type_details', {
                    paymentMethod: t(`payment_profile.${type}`),
                  })}
                </Heading>
              </Column>
              <Column span={[24, 18]}>
                <CreditCard
                  status={PAYMENT_PROFILE_STATUS[status]}
                  card={{
                    ...creditCard,
                    type: creditCard.cardType,
                  }}
                  variant={'primary'}
                />
              </Column>
            </Row>
            {!isAddressEmpty && (
              <Box marginTop={6}>
                <Row>
                  <Column span={[24, 6]}>
                    <Heading variant={'h4'}>
                      {t('payment_profile.billing_address_header')}
                    </Heading>
                  </Column>
                  <Column span={[24, 18]}>
                    <BillingAddress billingAddress={address} />
                  </Column>
                </Row>
              </Box>
            )}
          </>
        )}
      </Card.Body>
      <Card.Footer>
        <Box display={'flex'}>
          <Button.Group alignment="left">
            <Button
              color="danger"
              label={t('payment_profile.delete_payment_method', {
                paymentMethod: t(`payment_profile.${type}`),
              })}
              onClick={onDeletePaymentProfile}
            />
          </Button.Group>
          <Button.Group alignment="right">
            <Button
              color="secondary"
              label={t('close_preview', { ns: ['common'] })}
              onClick={() => onClose(false)}
            />
            {paymentProfileActions.includes(
              PaymentProfileActions.USE_FOR_SUBSCRIPTION_BILLING
            ) && (
              <Button
                color="primary"
                label={t('payment_profile.use_for_subscription')}
                isLoading={isLoading}
                disabled={isLoading || isError}
                onClick={() =>
                  changePaymentProfileService.send({
                    type: 'CHANGE_PAYMENT_PROFILE',
                    paymentProfileId,
                  })
                }
              />
            )}
          </Button.Group>
        </Box>
      </Card.Footer>
    </Card>
  );
};

export default Content;
